"use client";
import React, { useMemo } from "react";
import { cn } from "@/lib/utils";

export interface IAutoScrollSliderProps {
  children: React.ReactNode[];
  className?: string;
  swiperParent?: string;
  numberOfCards?: number;
  gradientClassName?: string;
  showGradient?: boolean;
  animationDuration?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isPaused?: boolean; // New prop for pause control
}

// import { IAutoScrollSliderProps } from "./interface";

const InfiniteLoopSlider = ({
  children,
  animationDuration,
  onMouseEnter,
  onMouseLeave,
  isPaused,
}: {
  children: any;
  animationDuration?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isPaused?: boolean;
}) => {
  return (
    <div
      className="relative box-border w-full overflow-hidden"
      key="loop"
      onMouseEnter={() => {
        if (onMouseEnter) onMouseEnter();
      }}
      onMouseLeave={() => {
        if (onMouseLeave) onMouseLeave();
      }}
    >
      <div
        className={cn(
          "autoscroll-marquee flex w-max flex-nowrap items-center gap-4 *:shrink-0",
        )}
        style={{
          animationDuration: animationDuration
            ? `${animationDuration}s`
            : `${Math.max(children.length * 1.5, 10)}s`,
          animationPlayState: isPaused ? "paused" : "running",
        }}
      >
        {children}
        {children}
      </div>
    </div>
  );
};

const AutoScrollSlider = ({
  children,
  numberOfCards = 10,
  swiperParent,
  gradientClassName = "",
  animationDuration,
  showGradient = false,
  isPaused = false,
}: IAutoScrollSliderProps) => {
  /*
   * Built during render, NOT in an effect.
   *
   * This was `useState(null)` + `useEffect`, so the component returned `<></>` on the server
   * and on the first client render, then inserted its whole subtree after hydration. For
   * AnnouncementBanner that meant the strip shipped as bare padding and grew to its real
   * height once the effect ran — a visible pop-in on every load. Duplicating an array is
   * pure work; it needs neither the browser nor state.
   */
  const updatedChildren = useMemo(() => {
    // create a copy of children (array)
    const original = Array.isArray(children) ? children.slice() : [children];

    // Guard BEFORE the loop. An empty `original` can never grow, so the while below would
    // spin forever. That hazard already existed, but inside an effect it only hung the tab
    // after hydration; computing during render would hang the SERVER instead.
    if (!original.length) return [];

    const newChildren: React.ReactNode[] = [...original];

    // ensure at least numberOfCards * 2 (so loop has room)
    while (newChildren.length < numberOfCards * 2) {
      newChildren.push(...original);
    }

    return newChildren;
  }, [children, numberOfCards]);

  return updatedChildren.length ? (
    <div className="relative w-full overflow-hidden">
      {showGradient && (
        <div
          className={`left-gradient absolute z-1 top-0 left-0 ${gradientClassName}`}
        />
      )}
      <div className={`relative overflow-hidden w-full ${swiperParent}`}>
        <InfiniteLoopSlider
          animationDuration={animationDuration}
          isPaused={isPaused}
        >
          {updatedChildren?.map((child, index) => (
            <div key={`autoscroll_${index}`} className="shrink-0">
              {child}
            </div>
          ))}
        </InfiniteLoopSlider>
      </div>
      {showGradient && (
        <div
          className={`right-gradient absolute z-1 top-0 right-0 ${gradientClassName}`}
        />
      )}
    </div>
  ) : (
    <></>
  );
};

export default AutoScrollSlider;
